import {flow, getEnv, getSnapshot, types} from "mobx-state-tree";
import {saveAs} from 'file-saver';
import {MediaFilesList} from "../../../models/mediaFiles/MediaFilesListModel";
import {TableColumn} from "../../tables/ScanbuyTable";
import {GroupSelectState} from "../../elements/groups/GroupSelectState";
import {ModalState} from "../../elements/modal/GenericModal";
import {AdvancedConfFilterSelectState} from "../../elements/filters/AdvancedConfFilterSelectState";
import {DimensionSelectViewState} from "../../elements/dimensions/dimensionSelectViewState";
import {TagFilterSelectState} from "../../elements/filters/TagFilterSelectState";
import {GroupFilterSelectState} from "../../elements/filters/GroupFilterSelectState";
import {TagModel as Tag} from "../../../models/tags/TagModel"
import lodash from "lodash";
import {filterSessionConstants, formConstants} from "../../../utils/constants";
import {CodeOwnerFilterSelectState} from "../../elements/filters/CodeOwnerFilterSelectState";
import {TagSelectState} from "../../elements/tags/TagSelectState";
import {
  applySearchParamsFromFilters,
  clearAllFilters,
  loadFiltersFromSession,
  persistFiltersToSession
} from "../../../utils/filterStorage";
import {resolveDownloadFilename} from "../../../utils/common";

export const MediaFilesListViewState = types.model('MediaFilesListViewState',{
    listModel: types.late(() =>(MediaFilesList)),
    columns: types.maybe(types.array(types.late(() => TableColumn))),
    groupSelectStore: types.maybe(types.reference(GroupSelectState)),
    codeOwnerFilterSelectState: types.maybeNull(types.reference(CodeOwnerFilterSelectState)),
    advFilterSelectState: types.maybeNull(types.reference(AdvancedConfFilterSelectState)),
    dimensionSelectViewState: types.maybeNull(types.reference(DimensionSelectViewState)),
    tagFilterSelectState: types.maybeNull(types.reference(TagFilterSelectState)),
    groupFilterSelectState: types.maybeNull(types.reference(GroupFilterSelectState)),
    modalStore: types.maybe(ModalState),
    tagSelectStore: types.maybeNull(types.reference(TagSelectState)),
    selectedTags: types.optional(types.array(types.reference(Tag, {acceptsUndefined: true})), []),
    isFilterTypeOR: types.optional(types.boolean, true),
    base64NonBrowserConvertedFile: types.optional(types.string, ''),
    isFilterVisible: types.optional(types.boolean, true),
    searchTxt : types.optional(types.string,''),
    isInitialLoad: types.optional(types.boolean, true),
    filterGroup: types.optional(types.boolean, true),
    optionalColumns: types.optional(types.array(types.string), []),
    columnNameMapping: types.optional(types.map(types.string), {}),
    tableOptionalColumns: types.optional(types.array(types.string), [])
}).volatile((self)=>({
    modalComponent: types.string,
    modalComponentStore: types.string,
    modalClassName: types.string,

})).actions((self) => ({
    handleRowClick(row, componentStore, component, modalClassName= "") {
        self.modalComponent = component;
        self.modalComponentStore = componentStore
        self.modalStore.handleTitle(componentStore.title)
        self.modalClassName= modalClassName;
        self.modalStore.toggle()
    },
    toggleFilterVisible() {
      self.isFilterVisible = !self.isFilterVisible
      self.saveFiltersToSessionStorage()
    },
    setColumns(col) {
        self.columns = col
    },
    setTableOptionalColumns(columns) {
      self.tableOptionalColumns = columns;
    },
    setBase64NonBrowserConvertedFile(str) {
      self.base64NonBrowserConvertedFile = str
    },
    fetchEpsAsBase64: flow(function* (epsUrl) {
      try {
        const manager = getEnv(self).mediaFileManager;
        const base64SrcImg = yield manager.convertEpsToBase64(epsUrl);
        self.setBase64NonBrowserConvertedFile(base64SrcImg);
      } catch (error) {

        console.error("Failed to fetch EPS as base64:", error);
      }
    }),
    downloadFile: flow(function* (uuid) {
      try {
        const manager = getEnv(self).mediaFileManager;
        const file = yield manager.getFileLocation(uuid);
        const originalUrl = file?.liveLinks?.original;
        const filename = resolveDownloadFilename(file?.name, originalUrl);
        if (originalUrl) {
          const blob = yield manager.downloadOriginalFile(originalUrl);
          saveAs(blob, filename);
        }
      } catch (error) {
        console.error('Failed to download media file:', error);
      }
    }),
    refresh() {
        self.listModel.fetchAll()
    },
    selectGroup(obj) {
      obj = obj || []
      self.listModel.addSearchParams({groupUuid: obj.flatMap((row) => row.uuid).join(",")})
      self.refresh()
    },
  setOnSelectChanges() {
    if (self.isInitialLoad) {
      self.isInitialLoad = false;
      return;
    }
    self.groupSelectStore.setMultiSelectConsumer(self.selectGroup)
    self.listModel.setSelectedGroup(self.groupSelectStore.selectedGroup) // used to filter list by group
    self.tagSelectStore?.setConsumer(self.selectTag)
    self.advFilterSelectState?.setConsumer(self.selectFilter)
    self.codeOwnerFilterSelectState?.setConsumer(self.selectFilter)
    self.dimensionSelectViewState?.setFilterConsumer(self.selectFilter)
    self.tagFilterSelectState?.setConsumer(self.selectFilter)
    self.groupFilterSelectState?.setConsumer(self.selectFilter)
    self.refresh()
    self.saveFiltersToSessionStorage()
  },
  onSearchChange(txt){
    console.log(txt?.currentTarget?.value);
    const searchText = txt?.currentTarget?.value;
    self.searchTxt = searchText
    if(searchText) {
      if (searchText.length > 2) {
        self.listModel.removeSearchParams(["q"]);
        self.listModel.addSearchParams({q: searchText})
        self.refresh()
      } else {
        self.listModel.removeSearchParams(["q"]);
        self.refresh()
      }
      self.searchTxt = searchText
    }
    },
    getMediaFileUrl(uuid){
      //call manager to get signed url
      //call manager to upload file
      try {
        const manager =  getEnv(self).mediaFileManager
        const res = manager.getS3BucketUrl(uuid)
        console.log("res success", res)
        return res
      }catch (e) {
        console.log("failed to get signed url: ", e.message);
        return e
      }

    },
    copyUrl(url){
      navigator.clipboard.writeText(url).then(() => {alert("'"+url+ "' was copied to your clipboard.")});
    },

    selectFilter(obj, covered){
      self.saveFiltersToSessionStorage()
      const shouldRemoveDimensions = covered.includes("dimensionUuid")
      const regex = shouldRemoveDimensions ? /^dimension/ : undefined;
      self.listModel.removeSearchParams(covered,regex);

      if(obj){
        const transpiledDimensions = {};
        if(!lodash.isEmpty(obj.dimensions)){
          obj.dimensions.forEach(dimension => {
            const [uuid, value] = dimension.split('_');
            if (transpiledDimensions.hasOwnProperty(`dimension[${uuid}]`)) {
              transpiledDimensions[`dimension[${uuid}]`] += ',' + value;
            } else {
              transpiledDimensions[`dimension[${uuid}]`] = value;
            }
          });
          delete obj.dimensions
        }

        const params = lodash.mapValues(obj,(value, key) => {
          return {}[key] = value.join(',');
        });
        self.listModel.addSearchParams({...params,...transpiledDimensions})
        self.refresh()
        const prevGroupUuid = self.prevGroupUuid || [];
        const currentGroupUuid = obj.groupUuid || [];
        if (!lodash.isEqual(prevGroupUuid, currentGroupUuid)) {
          self.filterGroup = false
          self.prevGroupUuid = currentGroupUuid;
        }
        self.getFilters();
      }
    },
    getFilters() {
      self.listModel.fetchFilters();
    },
    selectFilterMethod() {
      if (!self.isFilterTypeOR) {
        self.isFilterTypeOR = !self.isFilterTypeOR
        self.listModel.addSearchParams({tagFilterType: formConstants.Mode.OR})
      } else {
        self.isFilterTypeOR = !self.isFilterTypeOR
        self.listModel.addSearchParams({tagFilterType: formConstants.Mode.AND})
      }
      self.refresh()
    },
    selectTag(obj) {
      obj = obj || []
      self.listModel.addSearchParams({tags: obj.flatMap((row) => row.uuid).join(",")})
      self.refresh()
    },

    setColumnNameMapping(mapping) {
        self.columnNameMapping = mapping;
    },
    toggleColumnVisibility(columnId) {
        if (self.optionalColumns.includes(columnId)) {
            self.optionalColumns.replace(self.optionalColumns.filter(id => id !== columnId));
        } else {
            self.optionalColumns.push(columnId);
        }
        self.saveColumnsToSession();
    },
    saveColumnsToSession() {
        try {
            const columns = getSnapshot(self.optionalColumns);
            sessionStorage.setItem("mediaFilesListColumns", JSON.stringify(columns));
        } catch (e) {
            console.warn("Failed to save column visibility to sessionStorage", e);
        }
    },
    loadColumnsFromSession() {
        try {
            const saved = sessionStorage.getItem("mediaFilesListColumns");
            if (saved) {
                self.optionalColumns.replace(JSON.parse(saved));
            }
        } catch (e) {
            console.warn("Failed to load column settings from sessionStorage", e);
        }
    },
    // ifModalIsClosed(){
    //
    //  self.modalStore.open === false && self.setBase64NonBrowserConvertedFile('')
    // },
    afterCreate() {

    },
    saveFiltersToSessionStorage() {
      persistFiltersToSession(self, filterSessionConstants.CONFIG_SESSION_STORAGE_KEY);
    },
    loadFiltersFromSessionStorage() {
      loadFiltersFromSession(self, filterSessionConstants.CONFIG_SESSION_STORAGE_KEY);
      applySearchParamsFromFilters(self);
      self.isElasticSearchEnabled ? self.search() : self.refresh();
    },
    clearFilters() {
      clearAllFilters(self, filterSessionConstants.CONFIG_SESSION_STORAGE_KEY);
    },
    addSearchParamsFromLoadedFilters() {
      applySearchParamsFromFilters(self);
    },
})).views((self) => ({
  shouldRenderBase64NonBrowserConvertedFile(){
    return self.base64NonBrowserConvertedFile !== "" && self.base64NonBrowserConvertedFile !== undefined
  },
}))

